import * as React from 'react';
import { useCallback, useEffect, useState, useRef } from 'react';
import { TTypedTFunction, useTranslation } from '@lib/useTypedTranslation';
import { mapValues, mapKeys, isNil, map, get, isEmpty, Dictionary, isEqual, forEach, find } from 'lodash';
import { useRouteMatch, useLocation } from 'react-router-dom';
import { usePathBasedQueryString } from '@lib/usePathBasedQueryString';

import { IListCategory } from '../../components/listCard/header';
import { BatteryCentricService, IBatteryFilters, ISmartBatteryStatus } from '../../services/shared/battery/batteryCentricService';
import { ISortResult } from '../../services/shared/common';
import { getGroups, getHomeLocations, getModels, getBatteryPartNumbers, IGroups, IHomeLocation } from '../../services/config/config';
import { DataLoading } from '../../components/loading/dataLoading';
import { BatteryDetailsCard } from './compositions/batteryDetailsCard';

import { DataTable } from '../../components/data-table/dataTable';
import { PaginationBar } from '../../components/data-table/pagination/pagination';
import ETableHeader from '../../components/table/header';
import Header from '../../components/listCard/header';
import { InitialTableState, useTableReducer } from '../../components/data-table/lib';
import { useUserSettingsContext } from '../../context/userSettings';
import { formatBatteryWarrantyIcon, formatDateLastUpdated, showBatteryStatusTooltip, formatValue, getFormatPercentageToLocale, getFormatSecondsToHoursToLocale, getFormatNumberToLocale } from '../../components/data-table/dataFormatters';
import { IFilterResult, removeFilterItem, convertFilterToQuery } from '../../components/filters/filterLib';
import { StatusText } from '../../components/table/statusText';
import { ITableColumn, SortDirection } from '../../components/data-table/types';
import { FilterStatus, IFilterStatus } from '../../components/filters/newFilters/filterStatus';
import { Loading } from '../../components/loading/loading';
import { DeleteSmartBatteries } from '../../components/listCard/deleteSmartBatteries';
import { useCurrentUserContext } from '../../context/currentUser';
import { RequestInitWithRetry } from '../../lib/request';
import { useWorldRequest } from '../../lib/useWorldRequest';

import './index.css';
import { useWorldAction } from '../../lib/useWorldAction';
import classNames from 'classnames';
import { getLast30DaysTzDateParams } from '../../services/core/devicePerformance';
import { getDisplayTimeZone } from '../../lib/dateParams';
import moment from 'moment-timezone';
import { FilterContext } from '@components/data-table/filters/context';
import { GroupListFilter, HomeLocationsListFilter } from '../../containers/core/devices/compositions/filters/devicesListFilters';
import { BatteryPartNumberListFilter, DeviceModelListFilter } from './compositions/filters/batteryListFilters';
import { BatteryAlertsFilter } from '@components/data-table/filters/batteryAlertsFilter';
import { getNewStatusFilters, handleNewFilterRemoved as handleNewFilterRemovedUtil } from './filterUtils';
import { smartBatteryAlertStatuses } from '../../services/config/alertConstants';
import { useLanguageAndLocaleContext } from '../../context/languageAndLocale';

export type ListCategory = Omit<IListCategory, 'buildStatusMessage'>;

export const AppContext = React.createContext({
  setSelectedSmartBattery: null,
  getSelectedSmartBattery: null,
  getSelectedRow: null,
  batteryStatus: null
});

export const tableName = 'batteryList';
export const defaultPageSize = 20;
const service = new BatteryCentricService();

export const getBatteryColumnFromFilterGroup = (filterGroup: string): string => {
  switch (filterGroup) {
  case 'groups':
    return 'groups';
  case 'homeLocations':
  case 'homeLocation':
    return 'homeLocation';
  case 'batteryPartNumbers':
    return 'batteryPartNumber';
  case 'models':
    return 'model';
  case 'status':
  case 'alertStatus':
    return 'status'; // Both status and alertStatus map to the 'status' column
  default:
    throw new Error('Unknown filter group: ' + filterGroup);
  }
};

export function convertQueryStringToBatteryListFilter(queryString: string, t: TTypedTFunction): IFilterResult | undefined {
  const params = new URLSearchParams(queryString);
  const filter: IFilterResult = {};
  let hasFilters = false;

  params.forEach((value, key) => {
    const values = value.split(',').filter(Boolean);
    if (values.length === 0) return;

    if (key === 'groups') {
      filter[key] = values.map((group) => {
        if (group === '\\0' || group === 'no-group') {
          return { id: 'no-group', value: t('NO_GROUP_title'), nullFilter: true };
        }
        return group;
      });
      hasFilters = true;
    } else if (key === 'homeLocations') {
      filter[key] = values.map((loc) => {
        if (loc === '\\0' || loc === 'no-homelocation') {
          return { id: 'no-homelocation', value: t('NO_HOME_LOCATION'), nullFilter: true };
        }
        return loc;
      });
      hasFilters = true;
    } else if (key === 'batteryPartNumbers') {
      filter[key] = values;
      hasFilters = true;
    } else if (key === 'models') {
      filter[key] = values;
      hasFilters = true;
    } else if (key === 'status') {
      const statusMap: { [key: string]: string } = {
        'red': 'PROBLEM',
        'yellow': 'WARNING',
        'green': 'GOOD'
      };
      filter[key] = values.map(id => {
        const displayKey = statusMap[id];
        return displayKey ? { id, value: t(displayKey) } : undefined;
      }).filter(Boolean);
      hasFilters = true;
    } else if (key === 'alertStatus') {
      filter[key] = values.map(id => {
        // Expected format: alertType-level (e.g., 'batteryHealthStatus-red')
        const parts = id.split('-');
        if (parts.length === 2) {
          return id; // Store as string, will be matched against available filters
        }
        return undefined;
      }).filter(Boolean);
      hasFilters = true;
    }
  });

  return hasFilters ? filter : undefined;
}

interface Props {
  selectedId: string,
  selectedTimestamp: number
}

export function getFilterMap(filters: IFilterResult, homeLocationMap: Dictionary<string>): IBatteryFilters {
  const convertedFilters = convertFilterToQuery(filters);
  const allFilters: IBatteryFilters = { ...convertedFilters, underWarranty: false };
  if (!isEmpty(get(filters, 'underWarranty'))) {
    allFilters.underWarranty = true;
  }
  if (convertedFilters && convertedFilters.homeLocations) {
    const mappedLocations = map(convertedFilters.homeLocations as string[], (loc) => homeLocationMap[`${loc}`]);
    allFilters.homeLocations = mappedLocations;
  }
  return allFilters;
}


export function getHomeLocationName(value: string, rowData: ISmartBatteryStatus) {
  return rowData.deviceInfo?.homeLocation ? formatValue(rowData.deviceInfo.homeLocation.name) : "-";
}

export function getFilterGroupDisplayName(t: TTypedTFunction) {
  return (filterGroup: string, count: number): string => {
    switch (filterGroup) {
    case 'status':
      return t('BATTERY_STATUS_HEADER', { ns: 'batteryEssentials' });
    case 'alertStatus':
      return t('ALERT_STATUS', { count, ns: 'filterStatus' });
    case 'batteryPartNumbers':
      return t('BATTERY_PART_NUMBER', { count, ns: 'batteryEssentials' });
    case 'models':
      return t('MODEL', { count, ns: 'filterStatus' });
    case 'groups':
      return t('GROUP', { count, ns: 'filterStatus' });
    case 'homeLocations':
      return t('HOME_LOCATION', { count, ns: 'filterStatus' });
    default:
      return filterGroup;
    }
  };
}

const initialTableSortField: string = 'updated';
const initialTableSortColumn: number = 9;
const initialTableSortDirection: SortDirection = 'desc';
const initialSort = { column: initialTableSortColumn, direction: initialTableSortDirection, field: initialTableSortField };

// DataTableHeader always calls onSort with a defined numeric column index (never nil), but its
// direction can legitimately be nil (clicking a third time clears the sort). This defaults both
// defensively so a cleared/omitted value is always reported as an explicit 0/null rather than
// undefined.
export function resolveSortArgs(column: number, direction: SortDirection): [number, SortDirection] {
  return [!isNil(column) ? column : 0, !isNil(direction) ? direction : null];
}

export function BatteryCentricContainerBase(props: Props) {
  const ns = 'translation';
  const { t } = useTranslation(ns);
  const { locale } = useLanguageAndLocaleContext();

  const { queryString, prevQueryString } = usePathBasedQueryString(/\/core\/batteries/);

  const { tablePageSizes } = useUserSettingsContext();
  const { isEditor, isManager, isAdministrator } = useCurrentUserContext();

  const [selectedId, setSelectedId] = useState(props.selectedId || '-');
  const [idUpdateTimestamp, setIdUpdateTimestamp] = useState(props.selectedTimestamp || 0);
  const [listResetTimestamp, setListResetTimestamp] = useState<number>(undefined);
  const [currentFilter, setCurrentFilter] = useState(() => convertQueryStringToBatteryListFilter(queryString, t));
  const [statusMessage, setStatusMessage] = useState<'BATTERY' | 'SEARCH_MATCH_STATEMENT'>('BATTERY');
  const [resultStatus, setResultStatus] = useState<boolean>(false);
  const [homeLocationMapping, setHomeLocationMapping] = useState<Dictionary<string>>(undefined);
  const [availableFilterGroups, setAvailableFilterGroups] = useState<string[]>(undefined);
  const [availableFilterHomeLocations, setAvailableFilterHomeLocations] = useState<string[]>(undefined);
  const [availableFilterBatteryPartNumbers, setAvailableFilterBatteryPartNumbers] = useState<string[]>(undefined);
  const [availableFilterModels, setAvailableFilterModels] = useState<string[]>(undefined);
  const [groupsAndHomeLocationsLoaded, setGroupsAndHomeLocationsLoaded] = useState<boolean>(false);
  const [filtersLoaded, setFiltersLoaded] = useState<boolean>(false);
  const [showDeletePopup, setShowDeletePopup] = useState(false);
  const prevSelectedIdRef = useRef<string | undefined>(props.selectedId);

  const doGetList = useWorldAction(service.get);
  const doGetBatteryEstateDataWithFilters = useWorldAction(service.getBatteryEstateDataWithFilters);
  const doGetSmartBatteryDetails = useWorldAction(service.getSmartBatteryDetails);
  const doExport = useWorldAction(service.export);
  const doGetSmartBatteryLast30DaysDischargeTrend = useWorldAction(service.getSmartBatteryLast30DaysDischargeTrend);

  const batteryCentricDevicesDetails = classNames(
    'batteryCentricDetails_container',
    'col-md-12',
    'col-sm-12',
    'mt-sm-2',
    'mt-2',
    'mt-lg-0',
    "col-xl-12 col-lg-12",
  );


  const initialTableState: InitialTableState = {
    initialRows: 4,
    limit: tablePageSizes?.[tableName] || defaultPageSize,
    sort: initialSort
  };

  async function loadDetails(): Promise<any> {
    if (selectedId === '-') {
      return '-';
    }

    const smartBatteryDetails = await doGetSmartBatteryDetails(selectedId);
    const timeZone = getDisplayTimeZone(get(smartBatteryDetails, 'deviceInfo.timeZone.name', null));
    const dates = getLast30DaysTzDateParams(moment.utc(), timeZone);
    const smartBatteryLast30DaysDischargeTrend = await doGetSmartBatteryLast30DaysDischargeTrend(selectedId, dates);
    return { smartBatteryDetails, smartBatteryLast30DaysDischargeTrend };
  }

  const headerTitle = t('All_BATTERIES_TITLE', { ns: 'batteryEssentials' });

  const [
    { offset, limit, total, sort, dataRequestId, data, isLoading, search, selectedRow, checkedRows },
    { onSort, onPageChange, onPageSizeChange, onDataLoaded, onSearch, onRowSelected, onLoading, onCheckAll, onRowChecked }
  ] = useTableReducer<ISmartBatteryStatus>(tableName, initialTableState);

  async function exportList(): Promise<any> {
    return await doExport({ statusType: 'all', limit: 10000, offset: 0, sort: { field: sort.field, order: sort.direction }, search, pinnedId: props.selectedId, filters: getFilterMap(currentFilter, homeLocationMapping) });
  }


  const handleFilterRemoved = (id: string, item: IFilterStatus) => {
    handleNewFilterRemovedUtil(currentFilter, id, item, setCurrentFilter);
  };

  const handleClearFilters = () => {
    setCurrentFilter(undefined);
  };

  const handleSort = (column: number, direction: SortDirection, info: ITableColumn) => {
    const [resolvedColumn, resolvedDirection] = resolveSortArgs(column, direction);
    onSort(resolvedColumn, resolvedDirection, info);
  };

  const handleRowSelected = (rowIndex: number) => {
    const rowDataId = get(data[rowIndex], "id");
    if (rowIndex !== selectedRow) {
      setSelectedId(rowDataId);
      onRowSelected(rowIndex);
    }
  };

  const pinned = React.useMemo(() => {
    let pinned = undefined;
    const sortSet = (sort && !isEqual(sort, initialSort)) ? true : false;
    const filtersSet = (currentFilter && (Array.isArray(currentFilter?.groups) && currentFilter.groups.length > 0 || Array.isArray(currentFilter?.homeLocations) && currentFilter.homeLocations.length > 0 || Array.isArray(currentFilter?.batteryPartNumbers) && currentFilter.batteryPartNumbers.length > 0 || Array.isArray(currentFilter?.models) && currentFilter.models.length > 0 || Array.isArray(currentFilter?.status) && currentFilter.status.length > 0 || Array.isArray(currentFilter?.alertStatus) && currentFilter.alertStatus.length > 0)) ? true : false;
    if (!isNil(props.selectedId)) {
      pinned = (!search && !sortSet && !filtersSet) ? props.selectedId : undefined;
    }
    return pinned;
  }, [currentFilter, props.selectedId, search, sort]);

  const onGroupsAndHomeLocationsLoaded = useCallback(([groupsRaw, homeLocationsRaw]: [IGroups, IHomeLocation[]]) => {
    const groups = get(groupsRaw, 'groups.nodes', []).map((o: any) => o.name);
    let homeLocations;
    if (homeLocationsRaw) {
      homeLocations = homeLocationsRaw.map((o: any) => isNil(o.name) ? o.id : o.name);
      const homeLocationMapping = mapValues(mapKeys(homeLocationsRaw, (v) => isNil(v.name) ? v.id : v.name), (loc) => loc.id);
      setHomeLocationMapping(homeLocationMapping);
    }
    setAvailableFilterGroups(groups);
    setAvailableFilterHomeLocations(homeLocations);
    setGroupsAndHomeLocationsLoaded(true);
  }, []);

  const groupsAndHomeLocationsFetcher = useCallback(() => {
    return (options: RequestInitWithRetry) => {
      return Promise.all([
        getGroups()(options),
        getHomeLocations()(options)
      ]);
    };
  }, []);

  useWorldRequest(groupsAndHomeLocationsFetcher, { onSuccess: onGroupsAndHomeLocationsLoaded });

  const onFiltersLoaded = useCallback(([batteryPartNumbers, models]: [string[], string[]]) => {
    setAvailableFilterBatteryPartNumbers(batteryPartNumbers);
    setAvailableFilterModels(models);
    setFiltersLoaded(true);
  }, []);

  const filtersFetcher = useCallback(() => {
    return (options: RequestInitWithRetry) => {
      return Promise.all([
        getBatteryPartNumbers()(options),
        getModels()(options)
      ]);
    };
  }, []);

  useWorldRequest(filtersFetcher, { onSuccess: onFiltersLoaded });

  useEffect(() => {
    const newFilter = convertQueryStringToBatteryListFilter(queryString, t);
    if (prevQueryString !== queryString && !isEqual(currentFilter, newFilter)) {
      setCurrentFilter(newFilter);
    }
  }, [queryString, prevQueryString, currentFilter, t]);

  useEffect(() => {
    let selectedIndex = -1;
    if (!isLoading) {
      selectedIndex = data.findIndex(battery => battery?.id === selectedId);
    }
    if (props.selectedId && isNil(selectedRow) && !isLoading) {
      setSelectedId(props.selectedId);
      selectedIndex = 0;
    }
    if (selectedIndex !== selectedRow && !isLoading) {
      onRowSelected(selectedIndex);
    }
  }, [onRowSelected, selectedRow, isLoading, data, selectedId, props.selectedId]);

  useEffect(() => {
    async function getList(pinnedId: string, statusType: string, search: string, limit: number, offset: number, filters: IFilterResult, sort: ISortResult, homeLocationMap: Dictionary<string>) {
      return doGetList({ statusType, search, limit, offset, sort, pinnedId, filters: getFilterMap(filters, homeLocationMap) });
    }

    async function getCount(search: string, filters: IFilterResult, homeLocationMap: Dictionary<string>): Promise<number> {
      return await doGetBatteryEstateDataWithFilters(getFilterMap(filters, homeLocationMap), search);
    }

    if (isLoading && homeLocationMapping !== undefined) {
      Promise.all([
        getList(pinned, 'all', search, limit, offset, currentFilter, { field: sort.field, order: sort.direction }, homeLocationMapping),
        getCount(search, currentFilter, homeLocationMapping)
      ]).then(([list, count]) => {
        onDataLoaded(list, count, dataRequestId);
      });
    }
  }, [onDataLoaded, currentFilter, dataRequestId, homeLocationMapping, isLoading, limit, offset, pinned, search, sort, doGetList, doGetBatteryEstateDataWithFilters]);

  useEffect(() => {
    if (search) {
      setStatusMessage('SEARCH_MATCH_STATEMENT');
      setResultStatus(true);
    } else {
      setStatusMessage('BATTERY');
      setResultStatus(false);
    }
  }, [search]);

  useEffect(() => {
    if (props.selectedTimestamp && idUpdateTimestamp !== props.selectedTimestamp) {
      setIdUpdateTimestamp(props.selectedTimestamp);
      setListResetTimestamp(props.selectedTimestamp);
    }
  }, [idUpdateTimestamp, props.selectedTimestamp, selectedId]);

  useEffect(() => {
    onLoading();
  }, [onLoading, props.selectedId, props.selectedTimestamp, currentFilter]);

  useEffect(() => {
    // Only clear filter and search when selectedId actually changes, not on initial mount
    if (prevSelectedIdRef.current !== props.selectedId) {
      onSearch(undefined);
      setCurrentFilter(undefined);
      prevSelectedIdRef.current = props.selectedId;
    }
  }, [props.selectedId, onSearch]);

  const { path } = useRouteMatch();
  const location = useLocation();
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    // following condition handles batteries linked to deleted devices; this container only
    // mounts inside the Core Batteries hub now, so the hub's list route is the only path
    // pattern under which a click-through id can arrive
    if (params.has('id') && !props.selectedId && path.includes('/core/batteries/list')) {
      setSelectedId('-');
    }
  }, [props.selectedId, path, location.search]);


  const filterStatus = <FilterStatus
    selectedFilters={getNewStatusFilters(currentFilter)}
    onFilterRemoved={handleFilterRemoved}
    onClearFilters={handleClearFilters}
  />;

  const getBatteryStatusTooltipProcessor = (value: string, rowData: ISmartBatteryStatus) => {
    return showBatteryStatusTooltip(value, rowData, t);
  };

  const list = <DataTable
    data={data}
    columns={[
      { id: 'batterySerialNumber', title: t('BATTERY_SERIAL_NUMBER', { ns: 'batteryEssentials' }), dataPath: 'serialNumber', sortable: true, processor: formatBatteryWarrantyIcon },
      { id: 'batteryPartNumber', title: t('BATTERY_PART_NUMBER', { ns: 'batteryEssentials' }), dataPath: 'partNumber', sortable: true, processor: null, FilterForm: BatteryPartNumberListFilter },
      { id: 'status', title: t('STATUS'), dataPath: 'batteryStatus', sortable: false, processor: getBatteryStatusTooltipProcessor, textAlign: 'center', FilterForm: BatteryAlertsFilter },
      { id: 'health', title: t('HEALTH', { ns: 'batteryEssentials' }), dataPath: 'capacityFactor', sortable: true, processor: getFormatPercentageToLocale(locale), textAlign: 'right' },
      { id: 'cycleCount', title: t('CYCLE_COUNT', { ns: 'batteryEssentials' }), dataPath: 'cycleCount', sortable: true, processor: getFormatNumberToLocale(locale), textAlign: 'right' },
      { id: 'averageDischargeRate', title: t('AVERAGE_DISCHARGE_RATE', { ns: 'batteryEssentialsDeviceView' }), dataPath: 'averageDischarge', sortable: true, processor: getFormatPercentageToLocale(locale, 2), textAlign: 'right' },
      { id: 'averageDischargeTime', title: t('AVERAGE_DISCHARGE_TIME', { ns: 'translation' }), dataPath: 'averageDischargeTime', sortable: true, processor: getFormatSecondsToHoursToLocale(locale), textAlign: 'right' },
      { id: 'manufactureDate', title: t('MANUFACTURE_DATE', { ns: 'batteryEssentials' }), dataPath: 'mfd', sortable: true, processor: formatValue, textAlign: 'center' },
      { id: 'firstSeen', title: t('FIRST_SEEN'), dataPath: 'firstSeen', sortable: true, processor: formatDateLastUpdated, minWidth: '8rem' },
      { id: 'lastSeen', title: t('LAST_SEEN'), dataPath: 'updated', sortable: true, processor: formatDateLastUpdated, minWidth: '8rem' },
      { id: 'model', title: t('MODEL'), dataPath: 'deviceInfo.model', sortable: true, processor: null, FilterForm: DeviceModelListFilter, minWidth: '8rem' },
      { id: 'serialNumber', title: t('SERIAL_NUMBER'), dataPath: 'deviceInfo.serialNumber', sortable: true, processor: null },
      { id: 'imei', title: 'IMEI', dataPath: 'deviceInfo.imei', sortable: true, processor: null, minWidth: '10rem' },
      { id: 'assetTag', title: t('ASSET_TAG'), dataPath: 'deviceInfo.assetTag', sortable: true, processor: null, minWidth: '10rem' },
      { id: 'deviceName', title: t('DEVICE_NAME'), dataPath: 'deviceInfo.deviceName', sortable: true, processor: formatValue },
      { id: 'groups', title: t('GROUPS'), dataPath: 'deviceInfo.group', sortable: false, processor: formatValue, FilterForm: GroupListFilter, minWidth: '10rem' },
      { id: 'homeLocation', title: t('HOME_LOCATION'), dataPath: 'deviceInfo.homeLocationId', sortable: true, processor: getHomeLocationName, FilterForm: HomeLocationsListFilter }
    ]}
    className={isLoading ? '' : 'data-ready'}
    sortDirection={sort.direction}
    sortedColumnIndex={sort.column}
    options={{ rowIdDataPath: 'id' }}
    onSort={(column, direction, info) => handleSort(column, direction, info)}
    onRowSelected={handleRowSelected}
    selectedRow={selectedRow}
    onCheck={isEditor || isManager || isAdministrator ? onRowChecked : undefined}
    onCheckAll={isEditor || isManager || isAdministrator ? onCheckAll : undefined}
    checkedRows={checkedRows}
    stickyColumns={{ '0': 1, '1200px': 3 }}
  />;

  const selectedIdIndex = data?.findIndex(battery => battery?.id === selectedId);
  const selectedRowSet = selectedRow === selectedIdIndex;
  const isAllDataReady = groupsAndHomeLocationsLoaded && !isLoading && selectedRowSet;
  const checkedRowIds = checkedRows.map((e) => data[e]?.id);
  const deleteButtonProps = isEditor || isManager || isAdministrator ? {
    enabled: checkedRows.length > 0,
    checkedRowIds: checkedRowIds,
    onButtonClick: () => setShowDeletePopup(true)
  } : undefined;
  const isBatteryListData = !isLoading && data?.length > 0;

  const availableFilters = React.useMemo(() => {
    const nullGroupFilter = { id: 'no-group', value: t('NO_GROUP_title'), nullFilter: true };
    const nullHomeLocationFilter = { id: 'no-homelocation', value: t('NO_HOME_LOCATION'), nullFilter: true };
    const statusFilterValues = [
      { id: 'red', value: t('PROBLEM') },
      { id: 'yellow', value: t('WARNING') },
      { id: 'green', value: t('GOOD') }
    ];

    // Create battery alert status filter options
    const batteryAlertNames = {
      batteryHealthStatus: t('HEALTH', { ns: 'batteryEssentials' }),
      batteryAverageDischargeStatus: t('AVERAGE_DISCHARGE', { ns: 'translation' }),
      batteryCycleCountStatus: t('CYCLE_COUNT', { ns: 'batteryEssentials' })
    };
    const alertLevels = ['red', 'yellow', 'green'];
    const alertStatusFilterValues = smartBatteryAlertStatuses.flatMap(alertType =>
      alertLevels.map(level => ({
        id: `${alertType}-${level}`,
        value: `${batteryAlertNames[alertType]} (${statusFilterValues.find(s => s.id === level).value})`
      }))
    );

    return {
      groups: availableFilterGroups ? [nullGroupFilter, ...availableFilterGroups] : [],
      homeLocations: availableFilterHomeLocations ? [nullHomeLocationFilter, ...availableFilterHomeLocations] : [],
      batteryPartNumbers: availableFilterBatteryPartNumbers,
      models: availableFilterModels,
      status: statusFilterValues,
      alertStatus: alertStatusFilterValues
    };
  }, [availableFilterGroups, availableFilterHomeLocations, availableFilterBatteryPartNumbers, availableFilterModels, t]);

  return (<FilterContext.Provider value={{
    getColumnFromFilterGroup: getBatteryColumnFromFilterGroup,
    getFilterGroupDisplayName: getFilterGroupDisplayName(t),
    filters: currentFilter || {},
    handleFiltersUpdated: setCurrentFilter,
    handleCancel: () => {},
    filterOptions: {
      availableFilters,
      filtersLoaded: groupsAndHomeLocationsLoaded && filtersLoaded
    }
  }}>
    <div className={`batteryEssentialsTable_batteries_container ${isAllDataReady ? 'loaded' : ''}`}>
      <div className="row equal batteryCentric_batteries_estate">
        <div className={batteryCentricDevicesDetails}>
          <DataLoading key={`${selectedId}:${idUpdateTimestamp}`} loadData={loadDetails}>
            <BatteryDetailsCard id={selectedId} data={null} />
          </DataLoading>
        </div>
      </div>
      <div className="row batteryEssentialsTable_container batteryCentric" key={listResetTimestamp}>
        <div className="col-md-12">
          <div className="eTable-no-categories">
            <ETableHeader>
              <Header
                categories={[]}
                onExportClicked={exportList}
                exportButtonDisabled={!isBatteryListData}
                exportFileName='Batteries.csv'
                onSearchClicked={(input) => onSearch(input)}
                headerTitle={headerTitle}
                deleteButtonProps={deleteButtonProps}
                hideCategories={true} />
            </ETableHeader>
            {showDeletePopup && <DeleteSmartBatteries
              showPopup={showDeletePopup}
              handleClose={() => setShowDeletePopup(false)}
              batteryIds={deleteButtonProps.checkedRowIds}
              onBatteriesDeleted={onLoading}
            />}
            <div className="statusText" style={{ display: currentFilter && !isEqual(currentFilter, {}) ? 'block' : 'none' }}>
              {filterStatus}
            </div>
            <Loading isLoading={isLoading} transparentOverlay={false}>
              <div className='eTable_list'>
                {list}
                <PaginationBar
                  offset={offset}
                  limit={limit}
                  total={total}
                  onNewPage={(newOffset) => onPageChange(newOffset)}
                  onNewLimit={(newLimit) => onPageSizeChange(newLimit)}
                >
                  <StatusText
                    searchString={search}
                    total={total}
                    offset={offset}
                    limit={limit}
                    item={t(statusMessage, { count: total })}
                    fullStatusText={resultStatus}
                  />
                </PaginationBar>
              </div>
            </Loading>
          </div>
        </div>
      </div>
    </div>
  </FilterContext.Provider>);
}

export const BatteryCentricContainer = BatteryCentricContainerBase;
